import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import translations from 'services/translations/translations';

/**
 * A row of the calculation window. When the row order is being changed, the number of
 * the row is shown in front of it: typing another number moves the row to that place
 * (Enter or leaving the field), and the rows in between shift by one.
 */
const RowOrder = ({
  children,
  count,
  index,
  move,
  reordering,
}: {
  children: React.ReactNode;
  count: number;
  // The place of the row in its section (0-based); undefined for the heading row
  index?: number;
  move?: (fromIndex: number, toIndex: number) => void;
  reordering: boolean;
}): JSX.Element => {
  const { t } = useTranslation([translations.calculationWindow]);
  const number = index === undefined ? '' : String(index + 1);
  const [value, setValue] = useState(number);

  useEffect(() => setValue(number), [number]);

  if (!reordering) {
    return <>{children}</>;
  }

  const apply = () => {
    const newNumber = parseInt(value, 10);

    if (
      index === undefined ||
      move === undefined ||
      Number.isNaN(newNumber) ||
      newNumber === index + 1
    ) {
      setValue(number);
      return;
    }

    move(index, Math.min(Math.max(newNumber, 1), count) - 1);
  };

  return (
    <div className="row-order">
      <div className="row-order-number">
        {index === undefined ? null : (
          <input
            aria-label={t(`${translations.calculationWindow}:Row number`)}
            className="text-field"
            inputMode="numeric"
            onBlur={apply}
            onChange={(event) =>
              setValue(event.target.value.replace(/\D/g, ''))
            }
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }

              if (event.key === 'Escape') {
                setValue(number);
              }
            }}
            value={value}
          />
        )}
      </div>
      <div className="row-order-content">{children}</div>
    </div>
  );
};

export default RowOrder;
